import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { getNewApiClient } from "./client";
import { AuthenticationService } from "./authentication";

export { isPerspectiveAuthError } from "./auth_errors";

export class PerspectiveApiService {
  private static instance: PerspectiveApiService | undefined;
  // ONE live Apollo client for the whole app, held on the class rather than
  // on the instance. The provider and the default context each keep an
  // instance object; with the client on the instance, a reconnect only
  // reached whichever object it ran on, and the rest kept subscribing through
  // a stopped client carrying an expired token (SUNGLAS-39).
  private static client: ApolloClient<NormalizedCacheObject> | undefined;
  // The ID token in force when the current client was created. A socket's
  // connectionParams read the token when it opens, so a client older than
  // the stored token may still be carrying the previous one.
  private static tokenAtConnect: string | undefined;
  // Bumped on every reconnect so a recovery can tell whether the token
  // refresh it triggered already reopened the socket.
  private static generation = 0;
  private static recovering: Promise<boolean> | null = null;
  private static listeningForRefresh = false;

  private constructor() {}

  /**
   * `reset` recreates the client AND hands back a new instance object. The
   * identity change is a signal: the provider stores the instance in state
   * after a reconnect (customer switch), and effects keyed on it resubscribe
   * through the new client. A token refresh reconnects without it — nothing
   * about the data changed, only the socket.
   */
  public static getInstance(reset: boolean = false): PerspectiveApiService {
    if (!PerspectiveApiService.instance || reset) {
      PerspectiveApiService.reconnectClient();
      PerspectiveApiService.instance = new PerspectiveApiService();
    }
    if (!PerspectiveApiService.listeningForRefresh) {
      // Registered once, in the listener registry — the old single-slot
      // setter let any view that mounted later evict this handler, so the
      // client was never recreated on a scheduled refresh.
      PerspectiveApiService.listeningForRefresh = true;
      AuthenticationService.addOnTokenRefresh(() => {
        console.info(
          "Token refreshed, recreating PerspectiveApiService client",
        );
        PerspectiveApiService.reconnectClient();
      });
    }
    return PerspectiveApiService.instance;
  }

  private static reconnectClient(): void {
    PerspectiveApiService.client?.stop();
    PerspectiveApiService.client = getNewApiClient();
    PerspectiveApiService.tokenAtConnect = AuthenticationService.getIdToken();
    PerspectiveApiService.generation += 1;
  }

  private static currentClient(): ApolloClient<NormalizedCacheObject> {
    if (!PerspectiveApiService.client) {
      PerspectiveApiService.reconnectClient();
    }
    return PerspectiveApiService.client!;
  }

  /** Always the app's current client, whichever instance object asks. */
  getClient(): ApolloClient<NormalizedCacheObject> {
    return PerspectiveApiService.currentClient();
  }

  reset(): void {
    PerspectiveApiService.currentClient().resetStore();
    PerspectiveApiService.getInstance(true);
  }

  reconnect(): void {
    PerspectiveApiService.reconnectClient();
  }

  stop(): void {
    const client = PerspectiveApiService.currentClient();
    client.resetStore();
    client.stop();
  }

  /**
   * Recover from an auth rejection on a subscription. Resolves true only when
   * the socket will now carry a DIFFERENT token than the one that was
   * rejected — i.e. retrying can succeed. Never rejects.
   *
   * Reopening the socket stops every live subscription in the app, so it
   * happens only when it can help: after a successful refresh (through the
   * refresh listener), or when the stored token is already newer than the
   * one this client was created under. A failed refresh with nothing newer
   * to offer leaves the shared client alone. Concurrent callers share one
   * recovery.
   */
  async recoverAuth(): Promise<boolean> {
    if (!PerspectiveApiService.recovering) {
      PerspectiveApiService.recovering = (async () => {
        try {
          const before = PerspectiveApiService.generation;
          let refreshed = false;
          try {
            refreshed = (await AuthenticationService.refreshTokenNow()) != null;
          } catch (e) {
            console.error(
              "PerspectiveApiService.recoverAuth: refresh failed",
              e,
            );
          }
          if (PerspectiveApiService.generation !== before) {
            return true; // the refresh listener already rotated the socket
          }
          const stored = AuthenticationService.getIdToken();
          const storedIsNewer =
            stored != null && stored !== PerspectiveApiService.tokenAtConnect;
          if (refreshed || storedIsNewer) {
            PerspectiveApiService.reconnectClient();
            return true;
          }
          return false;
        } catch (e) {
          console.error("PerspectiveApiService.recoverAuth failed", e);
          return false;
        }
      })().finally(() => {
        PerspectiveApiService.recovering = null;
      });
    }
    return PerspectiveApiService.recovering;
  }
}
